/**
 * Custom Book Form JavaScript - Enhanced with Cascading Dropdowns
 * Handles:
 * - Auto-selection from PDP via sessionStorage
 * - State → City → Dealer cascading dropdowns
 * - Form submission to Google Sheets
 * - Form validation and user feedback
 */

class CustomBookForm {
  constructor(form) {
    this.form = form;
    this.productSelect = form.querySelector('[data-product-select]');
    this.stateSelect = form.querySelector('#stateSelect');
    this.citySelect = form.querySelector('#citySelect');
    this.dealerSelect = form.querySelector('#dealerSelect');
    this.dealerCodeInput = form.querySelector('#dealerCode');
    this.cityGroup = form.querySelector('#cityGroup');
    this.dealerGroup = form.querySelector('#dealerGroup');
    this.submitButton = form.querySelector('.custom-form-submit');
    this.buttonText = this.submitButton?.querySelector('.button-text');
    this.buttonLoader = this.submitButton?.querySelector('.button-loader');
    this.messageContainer = form.querySelector('#formMessage');
    this.googleSheetUrl = form.dataset.googleSheetUrl;
    
    // Dealer data storage
    this.dealersData = [];

    this.init();
  }

  init() {
    this.loadDealerData();
    this.handleSessionStorage();
    this.bindEvents();
  }

  async loadDealerData() {
    try {
      // Shopify asset URL - update path if different
      const dealerUrl = window.dealersJsonUrl || '/assets/dealers.json';
      
      const response = await fetch(dealerUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      this.dealersData = await response.json();
      
      console.log('✅ Dealer data loaded:', this.dealersData.length, 'records');
      
      // Populate states dropdown
      this.populateStates();
      
    } catch (error) {
      console.error('❌ Failed to load dealer data:', error);
      this.showMessage('Failed to load dealer locations. Please refresh the page.', 'error');
      
      this.setupFallbackStates();
    }
  }

  /**
   * Populate states dropdown from dealer data
   */
  populateStates() {
    if (!this.stateSelect || this.dealersData.length === 0) return;

    // Extract unique states and sort
    const states = [...new Set(
      this.dealersData
        .map(d => d.state)
        .filter(Boolean)
    )].sort();

    console.log('📍 Found states:', states.length);

    // Keep the first option (placeholder) and remove old state options
    const placeholder = this.stateSelect.querySelector('option[value=""]');
    this.stateSelect.innerHTML = '';
    if (placeholder) {
      this.stateSelect.appendChild(placeholder);
    } else {
      this.stateSelect.innerHTML = '<option value="">🗺️ Select Your State</option>';
    }

    // Add state options
    states.forEach(state => {
      const option = document.createElement('option');
      option.value = state;
      option.textContent = `🏛️ ${state}`;
      this.stateSelect.appendChild(option);
    });
  }

  /**
   * Fallback states if dealers.json fails to load
   */
  setupFallbackStates() {
    if (!this.stateSelect) return;

    const fallbackStates = [
      'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
      'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
      'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
      'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
      'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
      'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi'
    ];

    this.stateSelect.innerHTML = '<option value="">🗺️ Select Your State</option>';
    
    fallbackStates.forEach(state => {
      const option = document.createElement('option');
      option.value = state;
      option.textContent = `🏛️ ${state}`;
      this.stateSelect.appendChild(option);
    });

    console.log('⚠️ Using fallback states');
  }

  /**
   * Populate cities based on selected state
   */
  populateCities(selectedState) {
    if (!this.citySelect || !selectedState) return;

    console.log('🏙️ Populating cities for state:', selectedState);

    // Filter and get unique cities for the selected state
    const cities = [...new Set(
      this.dealersData
        .filter(d => d.state === selectedState)
        .map(d => d.city)
        .filter(Boolean)
    )].sort();

    console.log('📍 Found cities:', cities.length);

    // Clear city dropdown
    this.citySelect.innerHTML = '<option value="">🏙️ Select Your City</option>';

    // Add city options
    cities.forEach(city => {
      const option = document.createElement('option');
      option.value = city;
      option.textContent = `🏙️ ${city}`;
      this.citySelect.appendChild(option);
    });

    // Show city dropdown
    if (this.cityGroup) {
      this.cityGroup.style.display = 'block';
    }

    // Reset and hide dealer dropdown
    this.resetDealerDropdown();
  }

  /**
   * Populate dealers based on selected state and city
   */
  populateDealers(selectedState, selectedCity) {
    if (!this.dealerSelect || !selectedState || !selectedCity) return;

    console.log('🏪 Populating dealers for:', selectedState, '-', selectedCity);

    // Filter dealers for selected state and city
    const dealers = this.dealersData
      .filter(d => d.state === selectedState && d.city === selectedCity)
      .map(d => ({
        name: d.dealer,
        code: d['dealer code'] || d.dealer_code || ''
      }))
      .filter(d => d.name)
      .sort((a, b) => a.name.localeCompare(b.name));

    console.log('📍 Found dealers:', dealers.length);

    // Clear dealer dropdown
    this.dealerSelect.innerHTML = '<option value="">🏪 Select Your Dealer</option>';

    // Add dealer options
    dealers.forEach(dealer => {
      const option = document.createElement('option');
      option.value = dealer.name;
      option.textContent = `🏪 ${dealer.name}`;
      option.dataset.dealerCode = dealer.code;
      this.dealerSelect.appendChild(option);
    });

    // Show dealer dropdown
    if (this.dealerGroup) {
      this.dealerGroup.style.display = 'block';
    }
  }

  /**
   * Reset city dropdown
   */
  resetCityDropdown() {
    if (this.citySelect) {
      this.citySelect.innerHTML = '<option value="">Select Your City</option>';
    }
    if (this.cityGroup) {
      this.cityGroup.style.display = 'none';
    }
    this.resetDealerDropdown();
  }

  /**
   * Reset dealer dropdown
   */
  resetDealerDropdown() {
    if (this.dealerSelect) {
      this.dealerSelect.innerHTML = '<option value="">Select Your Dealer</option>';
    }
    if (this.dealerCodeInput) {
      this.dealerCodeInput.value = '';
    }
    if (this.dealerGroup) {
      this.dealerGroup.style.display = 'none';
    }
  }

  /**
   * Check sessionStorage and auto-select product if coming from PDP
   */
  handleSessionStorage() {
    try {
      const storedData = sessionStorage.getItem('bookTestRideData');
      
      if (!storedData) {
        console.log('No test ride data in sessionStorage');
        return;
      }

      const data = JSON.parse(storedData);
      // Check if data is recent (within last 5 minutes)
      const fiveMinutes = 5 * 60 * 1000;
      if (Date.now() - data.timestamp > fiveMinutes) {
        sessionStorage.removeItem('bookTestRideData');
        return;
      }

      // Auto-select the product
      if (data.variantId) {
        this.autoSelectProduct(data.variantId, data.productTitle, data.variantTitle);
        
        // Clear the sessionStorage after using it
        sessionStorage.removeItem('bookTestRideData');
      }

    } catch (error) {
      console.error('Error reading sessionStorage:', error);
    }
  }

  /**
   * Auto-select product variant and disable the dropdown
   */
  autoSelectProduct(variantId, productTitle, variantTitle) {
    if (!this.productSelect) {
      console.error('Product select not found');
      return;
    }
    const options = Array.from(this.productSelect.options);

    // Try to match by variant ID
    let matchingOption = options.find(option => {
      const match = option.dataset.variantId === String(variantId);
      if (match) console.log('Found variant ID match:', option.value);
      return match;
    });

    // Fallback: try to match by the full product value string
    if (!matchingOption && productTitle && variantTitle) {
      const searchString = `${productTitle} - ${variantTitle}`;
      matchingOption = options.find(option => {
        return option.value === searchString || option.textContent.trim() === searchString;
      });
      if (matchingOption) {
        console.log('Found match by product title:', matchingOption.value);
      }
    }

    if (matchingOption) {
      // Select the option
      matchingOption.selected = true;
      this.productSelect.value = matchingOption.value;
      
      // Create a hidden input to preserve the value when select is disabled
      let hiddenInput = this.form.querySelector('input[name="product"][type="hidden"]');
      if (!hiddenInput) {
        hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'product';
        this.form.appendChild(hiddenInput);
      }
      hiddenInput.value = matchingOption.value;
      
      // Disable the select to prevent changes
      this.productSelect.disabled = true;
      
      // Add visual indicator
      const formGroup = this.productSelect.closest('.custom-form-group');
      if (formGroup) {
        formGroup.classList.add('auto-selected');
      }

      console.log('✓ Successfully auto-selected product:', matchingOption.value);
      console.log('✓ Created hidden input with value:', hiddenInput.value);
    } else {
      console.error('❌ Could not find matching product option');
      console.log('Looking for variant ID:', variantId);
      console.log('Available options:', options.map(opt => ({
        value: opt.value,
        variantId: opt.dataset.variantId,
        text: opt.textContent
      })));
    }
  }

  /**
   * Bind form events
   */
  bindEvents() {
    // State selection handler
    if (this.stateSelect) {
      this.stateSelect.addEventListener('change', (e) => {
        const selectedState = e.target.value;
        
        if (selectedState) {
          this.populateCities(selectedState);
        } else {
          this.resetCityDropdown();
        }
      });
    }

    // City selection handler
    if (this.citySelect) {
      this.citySelect.addEventListener('change', (e) => {
        const selectedCity = e.target.value;
        const selectedState = this.stateSelect?.value;
        
        if (selectedCity && selectedState) {
          this.populateDealers(selectedState, selectedCity);
        } else {
          this.resetDealerDropdown();
        }
      });
    }

    // Dealer selection handler
    if (this.dealerSelect) {
      this.dealerSelect.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const dealerCode = selectedOption?.dataset?.dealerCode || '';
        
        if (this.dealerCodeInput) {
          this.dealerCodeInput.value = dealerCode;
          console.log('✓ Dealer code set:', dealerCode);
        }
      });
    }

    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  /**
   * Handle form submission
   */
  async handleSubmit(event) {
    event.preventDefault();

    // Validate Google Sheet URL
    if (!this.googleSheetUrl) {
      this.showMessage('Google Sheet URL is not configured. Please contact administrator.', 'error');
      return;
    }

    // Disable submit button
    this.setLoading(true);

    try {
      // Get form data
      const formData = new FormData(this.form);

      // Add timestamp and submission ID
      const submissionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      formData.append('submission_id', submissionId);
      formData.append('timestamp', new Date().toISOString());

      // Log submission data
      console.log('📤 Submitting form data:');
      for (const [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }

      // Convert to URL-encoded string
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        params.append(key, value);
      }

      // Submit to Google Sheets
      const response = await fetch(this.googleSheetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params.toString(),
        mode: 'no-cors' // Google Apps Script requires no-cors
      });

      // Since mode is 'no-cors', we won't get a readable response
      // Assume success if no error was thrown
      console.log('✅ Form submitted successfully');
      this.handleSuccess();

    } catch (error) {
      console.error('❌ Form submission error:', error);
      this.showMessage('Something went wrong! Please try again.', 'error');
      this.setLoading(false);
    }
  }

  /**
   * Handle successful form submission
   */
  handleSuccess() {
    this.showMessage('Form submitted successfully! We will contact you soon.', 'success');
    
    // Reset form after 2 seconds
    setTimeout(() => {
      // Remove hidden input if it exists
      const hiddenInput = this.form.querySelector('input[name="product"][type="hidden"]');
      if (hiddenInput) {
        hiddenInput.remove();
      }
      
      this.form.reset();
      
      // Re-enable product select if it was auto-selected
      if (this.productSelect && this.productSelect.disabled) {
        this.productSelect.disabled = false;
        const formGroup = this.productSelect.closest('.custom-form-group');
        if (formGroup) {
          formGroup.classList.remove('auto-selected');
        }
      }

      // Hide city and dealer dropdowns
      this.resetCityDropdown();
      
      this.setLoading(false);
      
      // Clear message after another 3 seconds
      setTimeout(() => {
        this.hideMessage();
      }, 3000);
    }, 2000);
  }

  /**
   * Show message to user
   */
  showMessage(message, type) {
    if (!this.messageContainer) return;

    this.messageContainer.textContent = message;
    this.messageContainer.className = `custom-form-message ${type}`;
    this.messageContainer.style.display = 'block';
  }

  /**
   * Hide message
   */
  hideMessage() {
    if (!this.messageContainer) return;
    this.messageContainer.style.display = 'none';
  }

  /**
   * Set loading state
   */
  setLoading(isLoading) {
    if (!this.submitButton || !this.buttonText || !this.buttonLoader) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.buttonText.style.display = 'none';
      this.buttonLoader.style.display = 'inline-flex';
    } else {
      this.submitButton.disabled = false;
      this.buttonText.style.display = 'inline';
      this.buttonLoader.style.display = 'none';
    }
  }
}

/**
 * Initialize form
 */
function initCustomBookForm() {
  const forms = document.querySelectorAll('#customBookForm');
  
  forms.forEach(form => {
    try {
      new CustomBookForm(form);
      console.log('✅ Custom book form initialized');
    } catch (error) {
      console.error('❌ Failed to initialize custom book form:', error);
    }
  });
}

/**
 * Main initialization
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomBookForm);
} else {
  initCustomBookForm();
}

/**
 * Handle Shopify theme editor
 */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const form = e.target.querySelector('#customBookForm');
    if (form) {
      try {
        new CustomBookForm(form);
        console.log('✅ Custom book form initialized in theme editor');
      } catch (error) {
        console.error('❌ Failed to initialize in theme editor:', error);
      }
    }
  });
}